/**
 * SizilPro — WizardStep3ProjectContext
 * Step 3 of the proposal wizard: project specifics.
 *
 * Updated to better match the current proposal flow:
 * - clearer template → proposal handoff messaging
 * - stronger AI-writing guidance
 * - builder mode + fixed-page-aware context hints
 *
 * File: src/modules/proposal-builder/components/wizard/WizardStep3ProjectContext.tsx
 */

import { useMemo, useState } from "react";
import {
  Loader2,
  ArrowLeft,
  Sparkles,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  ArrowRight,
  Wrench,
  FileStack,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { WizardAnswers } from "../../hooks/useProposalWizard";
import type {
  BuilderMode,
  ClientType,
  FixedPagePolicy,
} from "../../types";

const QUICK_PICKS: Record<ClientType, string[]> = {
  construction: [
    "Final construction clean",
    "Progress clean",
    "Post-renovation clean",
    "New build",
    "Commercial site",
    "Residential build",
    "Multi-unit project",
    "High-end finishes",
  ],
  restaurant: [
    "Daily service",
    "Weekly deep clean",
    "Front of house",
    "Back of house",
    "Kitchen equipment",
    "Restrooms included",
    "Hood cleaning",
    "Grease trap area",
  ],
  commercial: [
    "Daily service",
    "Weekly service",
    "Full building",
    "Common areas only",
    "Multiple floors",
    "Lobby and entrances",
    "Restrooms included",
    "After-hours only",
  ],
  residential: [
    "Recurring clean",
    "Deep clean",
    "Move-in clean",
    "Move-out clean",
    "Single family home",
    "Apartment",
    "Luxury property",
    "Pet-friendly",
  ],
  other: [
    "Recurring service",
    "One-time clean",
    "Large property",
    "Multiple locations",
    "Specialty service",
  ],
};

const FREQUENCY_OPTIONS = [
  { label: "Daily", value: "Daily" },
  { label: "Weekly", value: "Weekly" },
  { label: "Bi-weekly", value: "Bi-weekly" },
  { label: "Monthly", value: "Monthly" },
  { label: "One-time", value: "One-time" },
  { label: "Client's choice", value: "Client's discretion" },
];

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description:
      "This proposal is starting from a structured reusable template blueprint.",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description:
      "This proposal is starting from a template marked for the richer builder direction.",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-4 w-4" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description:
      "This proposal mainly relies on pages chosen directly by the template.",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description:
      "This proposal will inherit fixed pages defined in Brand Setup.",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description:
      "This proposal uses both template-selected pages and brand-defined fixed pages.",
  },
};

interface Props {
  answers: WizardAnswers;
  isGenerating: boolean;
  onSubmit: (values: {
    projectContext: string;
    additionalNotes: string;
  }) => void;
  onBack: () => void;
}

function getTemplateClientType(answers: WizardAnswers): ClientType {
  const raw =
    answers.template?.client_type ??
    (answers.template?.category as ClientType | null | undefined) ??
    "other";

  if (
    raw === "construction" ||
    raw === "restaurant" ||
    raw === "commercial" ||
    raw === "residential" ||
    raw === "other"
  ) {
    return raw;
  }

  return "other";
}

function getContextPlaceholder(clientType: ClientType) {
  return clientType === "construction"
    ? "e.g. Final construction clean for a new 4-story office building. High-end finishes, glass throughout, detail work on fixtures. Client wants a handoff-ready final presentation."
    : clientType === "restaurant"
      ? "e.g. Daily FOH and BOH cleaning for a 120-seat restaurant. Kitchen equipment included. Service starts after close. Client wants restrooms and entry glass checked every visit."
      : clientType === "commercial"
        ? "e.g. Full building clean for a 6-floor office complex. Common areas, restrooms, and lobby daily. Individual suites weekly. Access is after-hours only."
        : clientType === "residential"
          ? "e.g. Recurring weekly clean for a 4-bedroom home. Two adults, two dogs. Focus on kitchen and bathrooms. Client wants extra detail on floors and baseboards once per month."
          : "Describe the project in plain English — what needs to be cleaned, how often, where the work is happening, and anything specific the client cares about.";
}

export function WizardStep3ProjectContext({
  answers,
  isGenerating,
  onSubmit,
  onBack,
}: Props) {
  const [context, setContext] = useState(answers.projectContext);
  const [notes, setNotes] = useState(answers.additionalNotes);
  const [frequency, setFrequency] = useState("");
  const [error, setError] = useState("");

  const clientType = getTemplateClientType(answers);
  const quickPicks = QUICK_PICKS[clientType] ?? QUICK_PICKS.other;

  const builderCfg =
    BUILDER_MODE_CONFIG[answers.templateBuilderMode] ??
    BUILDER_MODE_CONFIG.prebuilt;

  const fixedCfg =
    FIXED_PAGE_POLICY_CONFIG[answers.templateFixedPagePolicy] ??
    FIXED_PAGE_POLICY_CONFIG.template_defined;

  const templateSummary = useMemo(() => {
    const uniqueStaticPageIds = Array.from(
      new Set(
        answers.templateStaticPageIds.filter(
          (pageId): pageId is string =>
            typeof pageId === "string" && pageId.trim().length > 0
        )
      )
    );

    return {
      templateName: answers.template?.name ?? "Selected template",
      templateDescription:
        answers.template?.description?.trim() ||
        "This template is already defining the starting services, pages, and proposal direction.",
      serviceCount: answers.lineItems.length,
      staticPageCount: uniqueStaticPageIds.length,
      aiTone: answers.template?.ai_tone ?? null,
      prospectName: answers.prospectName?.trim() || null,
      prospectAddress: [
        answers.prospectAddress,
        [answers.prospectCity, answers.prospectState].filter(Boolean).join(", "),
        answers.prospectZip,
      ]
        .filter(Boolean)
        .join(" "),
    };
  }, [answers]);

  const appendChip = (chip: string) => {
    setContext((prev) => {
      const trimmed = prev.trim();
      if (!trimmed) return chip;
      if (trimmed.toLowerCase().includes(chip.toLowerCase())) return prev;
      return `${trimmed}, ${chip.toLowerCase()}`;
    });
  };

  const selectFrequency = (freq: string) => {
    setFrequency(freq);
    setContext((prev) => {
      const trimmed = prev.trim();
      const cleaned = trimmed
        .replace(
          /,?\s*(daily|weekly|bi-weekly|monthly|one-time|client's discretion)( service)?/gi,
          ""
        )
        .trim();

      return cleaned
        ? `${cleaned}, ${freq.toLowerCase()} service`
        : `${freq.toLowerCase()} service`;
    });
  };

  const handleSubmit = () => {
    if (!context.trim()) {
      setError(
        "Tell me a bit about the project so I can write the best proposal."
      );
      return;
    }

    setError("");
    onSubmit({
      projectContext: context.trim(),
      additionalNotes: notes.trim(),
    });
  };

  const contextPlaceholder = getContextPlaceholder(clientType);
  const isEditMode = Boolean(
    answers.projectContext || answers.additionalNotes || answers.projectSummary
  );

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <p className="text-xs uppercase tracking-[0.14em] text-white/30">
          AI writing setup
        </p>
        <h2 className="mt-2 text-lg font-semibold text-white">
          Give the proposal real project context
        </h2>
        <p className="mt-2 text-sm leading-6 text-white/45">
          The better your notes are here, the better the AI can write the
          project summary and service scope. Focus on what the customer needs,
          where the work is happening, how often service is expected, and
          anything that makes this job different.
        </p>

        <div className="mt-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
          <p className="text-sm font-semibold text-white/85">
            {templateSummary.templateName}
          </p>
          <p className="mt-2 text-sm leading-6 text-white/45">
            {templateSummary.templateDescription}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                builderCfg.color
              )}
            >
              {builderCfg.icon}
              {builderCfg.label}
            </span>

            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                fixedCfg.color
              )}
            >
              {fixedCfg.icon}
              {fixedCfg.label}
            </span>

            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/40">
              <Wrench className="h-3.5 w-3.5" />
              {templateSummary.serviceCount} service
              {templateSummary.serviceCount !== 1 ? "s" : ""}
            </span>

            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/40">
              <FileStack className="h-3.5 w-3.5" />
              {templateSummary.staticPageCount} page
              {templateSummary.staticPageCount !== 1 ? "s" : ""}
            </span>

            {templateSummary.aiTone ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/40">
                <Sparkles className="h-3.5 w-3.5" />
                {templateSummary.aiTone}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <InfoCard
            title="Builder direction"
            description={builderCfg.description}
          />
          <InfoCard
            title="Fixed page direction"
            description={fixedCfg.description}
          />
        </div>
      </div>

      {(templateSummary.prospectName || templateSummary.prospectAddress) && (
        <div className="rounded-xl border border-white/10 bg-black/20 p-5">
          <p className="text-xs uppercase tracking-[0.14em] text-white/30">
            Client handoff
          </p>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <InfoCard
              title="Proposal for"
              description={templateSummary.prospectName ?? "Not provided yet"}
            />
            <InfoCard
              title="Job location"
              description={templateSummary.prospectAddress || "Not provided yet"}
            />
          </div>
        </div>
      )}

      <div>
        <p className="mb-3 text-xs uppercase tracking-[0.15em] text-white/30">
          Quick picks — tap to add
        </p>
        <div className="flex flex-wrap gap-2">
          {quickPicks.map((chip) => {
            const isActive = context.toLowerCase().includes(chip.toLowerCase());

            return (
              <button
                key={chip}
                type="button"
                onClick={() => appendChip(chip)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                  isActive
                    ? "border-white/30 bg-white/10 text-white"
                    : "border-white/10 text-white/40 hover:border-white/20 hover:bg-white/5 hover:text-white/70"
                )}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="mb-3 text-xs uppercase tracking-[0.15em] text-white/30">
          Service frequency
        </p>
        <div className="flex flex-wrap gap-2">
          {FREQUENCY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => selectFrequency(opt.value)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                frequency === opt.value
                  ? "border-white/30 bg-white/10 text-white"
                  : "border-white/10 text-white/40 hover:border-white/20 hover:bg-white/5 hover:text-white/70"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-white/70">
            Project details
            <span className="ml-1 text-red-400">*</span>
          </p>

          {context.length > 0 && (
            <button
              type="button"
              onClick={() => setContext("")}
              className="text-xs text-white/20 transition-colors hover:text-white/40"
            >
              Clear
            </button>
          )}
        </div>

        <textarea
          value={context}
          onChange={(e) => {
            setContext(e.target.value);
            setError("");
          }}
          placeholder={contextPlaceholder}
          rows={6}
          className={cn(
            "w-full resize-none rounded-xl border px-4 py-3 text-sm text-white placeholder:text-white/20 transition-colors focus:outline-none focus:ring-1",
            "bg-white/5",
            error
              ? "border-red-500/30 focus:ring-red-500/20"
              : "border-white/10 focus:border-white/20 focus:ring-white/20"
          )}
        />
        {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}

        <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
          <p className="text-xs uppercase tracking-[0.14em] text-white/30">
            Best results
          </p>
          <ul className="mt-2 space-y-1 text-sm leading-6 text-white/40">
            <li>Include what spaces or service areas are covered.</li>
            <li>Include how often service is expected.</li>
            <li>Include quality or presentation expectations.</li>
            <li>Include any scheduling, access, or site constraints.</li>
          </ul>
        </div>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-white/40">
          Anything else to include?
          <span className="ml-1 font-normal text-white/20">(optional)</span>
        </p>

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Special access instructions, parking, key contacts, specific requirements, things to avoid, or anything the client emphasized…"
          rows={3}
          className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/20 transition-colors focus:border-white/20 focus:outline-none focus:ring-1 focus:ring-white/20"
        />
      </div>

      <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 px-5 py-4">
        <p className="text-sm font-medium text-amber-300">
          What happens next
        </p>
        <p className="mt-1 text-sm leading-6 text-white/45">
          {isEditMode
            ? "The next step is review. AI will refresh the proposal summary and service scope using your updated project details, then you’ll review everything before returning to proposal detail."
            : "The next step is review. AI will draft the proposal summary and service scope using your project details, then you’ll review everything before moving into proposal detail, PDF, send, and sign flow."}
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <p className="text-xs leading-relaxed text-white/40">
          I’ll use these project details plus your{" "}
          <span className="text-white/60">
            {answers.template?.name ?? "selected"}
          </span>{" "}
          template to write a polished project summary and draft scope
          descriptions for each service in the proposal.
        </p>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isGenerating}
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-white/40 transition-colors hover:text-white/70 disabled:opacity-30"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isGenerating || !context.trim()}
          className="flex items-center gap-2 rounded-xl bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Writing proposal…
            </>
          ) : (
            <>
              <ArrowRight className="h-4 w-4" />
              Continue to review
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function InfoCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
        {title}
      </p>
      <p className="mt-2 text-sm leading-6 text-white/45">{description}</p>
    </div>
  );
}